/**
 * Root app: React Query, theme, branding, and router.
 */

import { useEffect } from 'react';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Router } from './Router';
import { useThemeStore } from '@/stores/themeStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import type { SidebarConfig } from '@/types/preferences.types';
import '@/styles/layout.css';

/** Shared React Query client. Queries refetch on use; mutations refresh dashboard stats/charts. */
const queryClient: QueryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboardCharts'] });
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0,
      refetchOnWindowFocus: false,
    },
  },
});

/** Applies persisted theme + sidebar prefs, then mounts the router. */
function AppContent() {
  const { setDarkMode, applyTheme } = useThemeStore();
  const { loadSidebarState } = useLayoutStore();
  const isDarkMode = usePreferencesStore((state) => state.preferences?.isDarkMode);
  const sidebarConfig = usePreferencesStore(
    (state) => state.preferences?.interfaceConfigs?.sidebar,
  );

  useEffect(() => {
    if (isDarkMode !== undefined) {
      setDarkMode(isDarkMode);
    }
  }, [isDarkMode, setDarkMode]);

  // Load sidebar state only when the sidebar config itself changes,
  // not on every preferences update (e.g. mode switch updates global.appMode
  // which would otherwise re-collapse the sidebar before the debounced save fires).
  useEffect(() => {
    if (sidebarConfig) {
      loadSidebarState(sidebarConfig as SidebarConfig);
    }
  }, [sidebarConfig, loadSidebarState]);

  useEffect(() => {
    applyTheme();
  }, [applyTheme]);

  return <Router />;
}

/** Root app: React Query provider around theme, branding, and router. */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}
